import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'

import {
  RequestCompare,
  RequestMemberReportList,
  RequestReport,
  RequestReportList,
  RequestTeamReportList,
  RequestTeamSort,
  ResponseCompare,
  ResponseMemberReportList,
  ResponseMonthlyMemberReport,
  ResponseMonthlyTeamReport,
  ResponseReportList,
  ResponseTeamReportList,
  ResponseWeeklyMemberReport,
  ResponseWeeklyTeamReport,
} from '../../types/report'

import {
  getCompare,
  getMemberReportList,
  getMonthlyMemberReport,
  getMonthlyTeamReport,
  getReportList,
  getTeamReportList,
  getWeeklyMemberReport,
  getWeeklyTeamReport,
  patchTeamOrder,
} from '../../apis/report'

interface State {
  isLoading: boolean
  isLoadingCompare: boolean
  isLoadingReportList: boolean
  memberReportList: ResponseMemberReportList | undefined
  teamReportList: ResponseTeamReportList | undefined
  monthlyMemberReport: ResponseMonthlyMemberReport | undefined
  monthlyTeamReport: ResponseMonthlyTeamReport | undefined
  weeklyMemberReport: ResponseWeeklyMemberReport | undefined
  weeklyTeamReport: ResponseWeeklyTeamReport | undefined
  compare: ResponseCompare | undefined
  reportList: ResponseReportList | undefined
}

const initialState: Partial<State> = {
  isLoading: true,
  isLoadingCompare: false,
  isLoadingReportList: false,
  memberReportList: undefined,
  teamReportList: undefined,
  monthlyMemberReport: undefined,
  monthlyTeamReport: undefined,
  weeklyMemberReport: undefined,
  weeklyTeamReport: undefined,
  compare: undefined,
  reportList: undefined,
}

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    requestStartCompare: (state) => {
      state.isLoadingCompare = true
      return state
    },
    requestStartReportList: (state) => {
      state.isLoadingReportList = true
      return state
    },
    requestStartMemberReportList: (state) => {
      state.isLoading = true
      return state
    },
    buildMemberReportList: (state, action: PayloadAction<ResponseMemberReportList>) => {
      state.isLoading = false
      state.memberReportList = action.payload
      return state
    },
    buildTeamReportList: (state, action: PayloadAction<ResponseTeamReportList>) => {
      state.isLoading = false
      state.teamReportList = action.payload
      return state
    },
    buildMonthlyMemberReport: (state, action: PayloadAction<ResponseMonthlyMemberReport>) => {
      state.isLoading = false
      state.monthlyMemberReport = action.payload
      return state
    },
    buildMonthlyTeamReport: (state, action: PayloadAction<ResponseMonthlyTeamReport>) => {
      state.isLoading = false
      state.monthlyTeamReport = action.payload
      return state
    },
    buildWeeklyMemberReport: (state, action: PayloadAction<ResponseWeeklyMemberReport>) => {
      state.isLoading = false
      state.weeklyMemberReport = action.payload
      return state
    },
    buildWeeklyTeamReport: (state, action: PayloadAction<ResponseWeeklyTeamReport>) => {
      state.isLoading = false
      state.weeklyTeamReport = action.payload
      return state
    },
    buildCompare: (state, action: PayloadAction<ResponseCompare>) => {
      state.isLoadingCompare = false
      state.compare = action.payload
      return state
    },
    buildReportList: (state, action: PayloadAction<ResponseReportList>) => {
      state.isLoadingReportList = false
      state.reportList = action.payload
      return state
    },
    buildReadReportList: (state, action: PayloadAction<{id: number, target: string, type: string, date: string}>) => {
      if (!state.reportList) return state
      const tmpReportList = { ...state.reportList }
      // クリック時にレポートに対して既読フラグを設定する
      tmpReportList.teams?.forEach((team) => {
        if (action.payload.target === "team") {
          if (team.id !== action.payload.id) return
          if (action.payload.type === "monthly") {
            team.reports.is_read = true
            return
          }
          team.reports.weekly_reports.forEach((report) => {
            if (report.date !== action.payload.date) return
            report.is_read = true
          })
        } else {
          team.members.forEach((member) => {
            if (member.id !== action.payload.id) return
            if (action.payload.type === "monthly") {
              member.reports.is_read = true
              return
            }
            member.reports.weekly_reports.forEach((report) => {
              if (report.date !== action.payload.date) return
              report.is_read = true
            })
          })
        }
      })
      state.reportList = tmpReportList
      state.isLoadingReportList = false
      return state
    },
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestStart,
  requestStartCompare,
  requestStartReportList,
  requestStartMemberReportList,
  buildReportList,
  buildMemberReportList,
  buildTeamReportList,
  buildMonthlyMemberReport,
  buildMonthlyTeamReport,
  buildWeeklyMemberReport,
  buildWeeklyTeamReport,
  buildCompare,
  buildReadReportList,
  resetState,
} = reportSlice.actions

export const reportReducer = reportSlice.reducer

export const fetchReportList = (
  param: RequestReportList,
): AppThunk => async (dispatch) => {
  dispatch(requestStartReportList())
  getReportList(param)
    .then((result: ResponseReportList) => {
      dispatch(buildReportList(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMemberReportList = (
  param: RequestMemberReportList,
): AppThunk => async (dispatch) => {
  dispatch(requestStartMemberReportList())
  getMemberReportList(param)
    .then((result: ResponseMemberReportList) => {
      dispatch(buildMemberReportList(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchTeamReportList = (
  param: RequestTeamReportList,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getTeamReportList(param)
    .then((result: ResponseTeamReportList) => {
      dispatch(buildTeamReportList(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMonthlyMemberReport = (
  id: number,
  param: RequestReport,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMonthlyMemberReport(id, param)
    .then((result: ResponseMonthlyMemberReport) => {
      dispatch(buildMonthlyMemberReport(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMonthlyTeamReport = (
  id: number,
  param: RequestReport,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMonthlyTeamReport(id, param)
    .then((result: ResponseMonthlyTeamReport) => {
      dispatch(buildMonthlyTeamReport(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyMemberReport = (
  id: number,
  param: RequestReport,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getWeeklyMemberReport(id, param)
    .then((result: ResponseWeeklyMemberReport) => {
      dispatch(buildWeeklyMemberReport(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyTeamReport = (
  id: number,
  param: RequestReport,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getWeeklyTeamReport(id, param)
    .then((result: ResponseWeeklyMemberReport) => {
      dispatch(buildWeeklyTeamReport(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchCompare = (
  param: RequestCompare,
): AppThunk => async (dispatch) => {
  dispatch(requestStartCompare())
  getCompare(param)
    .then((result: ResponseCompare) => {
      dispatch(buildCompare(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateTeamOrder = (
  params: RequestTeamSort,
): AppThunk => async () => {
  patchTeamOrder(params)
}

export const updateReadReport = (
  params: {id: number, target: string, type: string, date: string},
): AppThunk => async (dispatch) => {
  dispatch(buildReadReportList(params))
}
